import React, { useState, useMemo, useEffect } from "react";
import Select from "react-select";
import { Icon } from "@iconify/react/dist/iconify.js";
import { useConfig } from "../utils/ConfigContext.jsx";
import { createAxiosInstance } from "../utils/AxiosInstance.jsx";
import { useSelector } from "react-redux";
import axios from "axios";
import { toast } from "react-hot-toast";
import { useNavigate } from 'react-router-dom'

const UserForm = ({ mode, id }) => {
    const { API_BASE_URL } = useConfig();
    const AxiosInstance = createAxiosInstance(API_BASE_URL);
    const userData = useSelector((state) => state.auth.userData);
    const [roleOptions, setRoleOptions] = useState([]);
    const [moduleOptions, setModuleOptions] = useState([]);
    const [roleModulesMap, setRoleModulesMap] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [imagePreviewUrl, setImagePreviewUrl] = useState("");
    // HRM Modal State
    const [showHrmModal, setShowHrmModal] = useState(false);
    const [searchEmail, setSearchEmail] = useState("");
    const [isSearching, setIsSearching] = useState(false);
    const [hrmUser, setHrmUser] = useState(null);
    const navigate = useNavigate();

    const centersData = userData.hospitals;
    const ALL_CENTERS = centersData.map((center) => ({ hm_id: center.hm_id, hm_name: center.hm_name, type: center.ht_name }));
    const centerOptions = ALL_CENTERS.map((c) => ({
        value: c.hm_id,
        label: `${c.hm_name} (${c.type})`,
        type: c.type,
    }));
    const ACCESS_OPTIONS = ["Custom", "All", ...new Set(ALL_CENTERS.map(c => c.type))]

    const SALUTATION_OPTIONS = [
        { value: "Mr.", label: "Mr." },
        { value: "Ms.", label: "Ms." },
        { value: "Mrs.", label: "Mrs." },
    ];

    const [formData, setFormData] = useState({
        salutation: "Mr.",
        firstName: "",
        lastName: "",
        email: "",
        address: "",
        designation: "",
        phoneNumber: "",
        username: "",
        password: "",
        role: "",
        accessCenter: ["Custom"],
        selectedCenters: [],
        selectedModules: [],
        profilePhoto: '',
    });

    const [errors, setErrors] = useState({});
    const [showPassword, setShowPassword] = useState(false);


    const validateForm = () => {
        const newErrors = {};

        if (!formData.firstName.trim()) newErrors.firstName = "First name is required";
        if (!formData.email.trim()) {
            newErrors.email = "Email is required";
        } else if (!/^\S+@\S+\.\S+$/.test(formData.email)) {
            newErrors.email = "Invalid email format";
        }

        if (!formData.phoneNumber.trim()) {
            newErrors.phoneNumber = "Phone number is required";
        } else if (!/^\d{10}$/.test(formData.phoneNumber)) {
            newErrors.phoneNumber = "Phone number must be 10 digits";
        }

        if (!formData.designation.trim()) newErrors.designation = "Designation is required";
        if (!formData.username.trim()) newErrors.username = "Username is required";
        if (!formData.password && mode !== "Edit") {
            newErrors.password = "Password is required";
        } else if (formData.password && formData.password.length < 6) {
            newErrors.password = "Password must be at least 6 characters";
        }

        if (!formData.role) newErrors.role = "Please select a role";
        if (!formData.selectedModules.length) newErrors.selectedModules = "Select at least one module";
        if (!formData.selectedCenters.length) newErrors.selectedCenters = "Select at least one center";

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const generatePassword = () => {
        const chars = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*()_+~`|}{[]:;?><,./-=";
        let password = "";
        for (let i = 0; i < 12; i++) {
            const randomIndex = Math.floor(Math.random() * chars.length);
            password += chars[randomIndex];
        }
        setFormData((prev) => ({ ...prev, password }));
        setShowPassword(true);
        toast.success("Password generated successfully!", {
            duration: 3000,
            style: {
                background: '#10b981',
                color: '#fff',
                fontSize: '14px',
                fontWeight: '500',
                borderRadius: '8px',
                padding: '16px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            },
            position: 'top-right',
            icon: '✓',
        });
    };

    const copyPasswordToClipboard = () => {
        if (formData.password) {
            navigator.clipboard.writeText(formData.password);
            toast.success("Password copied to clipboard!", {
                duration: 3000,
                style: {
                    background: '#10b981',
                    color: '#fff',
                    fontSize: '14px',
                    fontWeight: '500',
                    borderRadius: '8px',
                    padding: '16px',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                },
                position: 'top-right',
                icon: '✓',
            });
        }
    };



    const fetchModules = async () => {
        try {
            const res = await AxiosInstance.get('/modules/assignable');
            const moduleData = res.data?.data || [];
            const options = moduleData.map((m, i) => ({
                value: m.mm_id,
                label: m.mm_module_name,
            }))
            setModuleOptions(options);

        }
        catch (error) {
            console.error("Error fetching roles:", error);
        }


    }
    const fetchRolesModules = async () => {
        try {
            const res = await AxiosInstance.get('/roles');
            const rolesData = res.data?.data || [];

            // radio options
            const roleOpt = rolesData.map(r => ({
                id: r.id,
                name: r.name,
            }));
            setRoleOptions(roleOpt);

            // ROLE → MODULE IDS mapping
            const roleModuleMapping = {};
            rolesData.forEach(role => {
                roleModuleMapping[role.id] = role.modules.map(m => m.mm_id);
            });

            setRoleModulesMap(roleModuleMapping);
        } catch (error) {
            console.error("Error fetching roles:", error);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((p) => ({ ...p, [name]: value }));
    };
    const handleRoleChange = (e) => {
        const roleId = Number(e.target.value);

        setFormData(prev => ({
            ...prev,
            role: roleId,
            selectedModules: roleModulesMap[roleId] || [],
        }));
    };

    const handleModulesChange = (selected) => {
        setFormData(prev => ({
            ...prev,
            selectedModules: selected.map(o => o.value),
        }));
    };

    const handleAccessTypeChange = (e) => {
        const { value: option, checked } = e.target;

        setFormData((prev) => {
            let nextAccess = prev.accessCenter || [];

            if (option === "All") {
                nextAccess = checked ? ["All"] : [];
            } else {
                nextAccess = checked
                    ? [...nextAccess.filter(v => v !== "All"), option]
                    : nextAccess.filter(v => v !== option);
            }

            const selectedTypes = nextAccess.filter(v => v !== "All" && v !== "Custom");
            let nextCenters = prev.selectedCenters;

            if (nextAccess.includes("All")) {
                nextCenters = centerOptions.map(c => c.value);
            } else if (selectedTypes.length > 0) {
                nextCenters = centerOptions
                    .filter(c => selectedTypes.includes(c.type))
                    .map(c => c.value);
            } else if (!nextAccess.includes("Custom")) {
                nextCenters = [];
            }

            return {
                ...prev,
                accessCenter: nextAccess,
                selectedCenters: nextCenters,
            };
        });
    };

    const handleCentersChange = (selected) => {
        setFormData((p) => ({
            ...p,
            selectedCenters: selected ? selected.map(s => s.value) : [],
        }));
    };

    // === HRM API ===
    const fetchFromHrm = async () => {
        if (!searchEmail) {
            alert("Please enter email");
            return;
        }

        try {
            setIsSearching(true);
            const res = await AxiosInstance.post('/fetchHrm',{
                email:searchEmail
            })


            if (!res.data || res.data.status === false) {
                throw new Error("User not found in HRM");
            }
            const UserData = res.data?.data || [];
            // ✅ Store HRM user in modal
            setHrmUser(UserData);

            setSearchEmail("");

        }
        catch (error) {
            // axios error handling
            if (error.response) {
                console.error("API Error:", error.response.data);
            } else {
                console.error("Network Error:", error.message);
            }
        } finally {
            setIsSearching(false);
        }
    };
    const handleSelectHrmUser = () => {
        if (!hrmUser) return;

        setFormData((prev) => ({
            ...prev,
            salutation: hrmUser.salutation,
            firstName: hrmUser.first_name,
            lastName: hrmUser.last_name,
            email: hrmUser.email,
            phoneNumber: hrmUser.mobile_no,
            designation: hrmUser.designation_name,
            address: hrmUser.address,
            username: hrmUser.user_name,
            profilePhoto: hrmUser.um_image


        }));
        setShowHrmModal(false);
        setSearchEmail("");
        setHrmUser(null);
        setImagePreviewUrl(hrmUser.um_image);
    };

    // === Submit ===
    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        if (!validateForm()) {
            setIsSubmitting(false); return;
        }

        try {
            const endpoint = mode === "Edit" && id ? `/users/${id}` : '/users';
            const method = mode === "Edit" && id ? 'patch' : 'post';
            const res = await AxiosInstance[method](endpoint, formData);

            if (res.data && res.data.status === true) {
                toast.success(mode === "Edit" ? "User updated successfully!" : "User saved successfully!", {
                    duration: 3000,
                    style: {
                        background: '#10b981',
                        color: '#fff',
                        fontSize: '14px',
                        fontWeight: '500',
                        borderRadius: '8px',
                        padding: '16px',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                    },
                    position: 'top-right',
                    icon: '✓',
                });
            }
            setIsSubmitting(false);
            navigate('/users');

        }
        catch (error) {
            setIsSubmitting(false);

            if (error.response) {
                // Handle 422 Unprocessable Entity - Validation Errors
                if (error.response.status === 422) {
                    const validationErrors = error.response.data?.errors || {};
                    setErrors(validationErrors);

                    // Show validation errors as toast
                    const errorMessages = Object.entries(validationErrors)
                        .map(([field, messages]) => {
                            const msgArray = Array.isArray(messages) ? messages : [messages];
                            return `${field}: ${msgArray.join(', ')}`;
                        })
                        .join('\n');

                    toast.error(errorMessages, {
                        duration: 5000,
                        style: {
                            background: '#ef4444',
                            color: '#fff',
                            fontSize: '14px',
                            fontWeight: '500',
                            borderRadius: '8px',
                            padding: '16px',
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                        },
                        position: 'top-right',
                        icon: '✕',
                    });
                } else {

                    toast.error(error.response.data?.message || "Failed to create user", {
                        duration: 5000,
                        style: {
                            background: '#ef4444',
                            color: '#fff',
                            fontSize: '14px',
                            fontWeight: '500',
                            borderRadius: '8px',
                            padding: '16px',
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                        },
                        position: 'top-right',
                        icon: '✕',
                    });
                }
            } else {

                toast.error("Network Error: " + error.message, {
                    duration: 5000,
                    style: {
                        background: '#ef4444',
                        color: '#fff',
                        fontSize: '14px',
                        fontWeight: '500',
                        borderRadius: '8px',
                        padding: '16px',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                    },
                    position: 'top-right',
                    icon: '✕',
                });
            }
        }
    };

    const fetchUserData = async () => {
        try {
            const res = await AxiosInstance.get(`/users/${id}`);
            const user = res.data?.data || res.data;

            setFormData({
                salutation: user.salutation || "Mr.",
                firstName: user.first_name || "",
                lastName: user.last_name || "",
                email: user.email || "",
                address: user.address || "",
                designation: user.designation || "",
                phoneNumber: user.phone || "",
                username: user.user_name || "",
                password: "",
                role: user.role_id || "",
                accessCenter: ["Custom"],
                selectedCenters: user.hospitals ? user.hospitals.map(c => c.hm_id || c) : [],
                selectedModules: user.modules ? user.modules.map(m => m.mm_id || m) : [],
                profilePhoto: user.profile_picture || '',
            });

            if (user.profile_picture || user.um_image) {
                setImagePreviewUrl(user.profile_picture || user.um_image);
            }
        } catch (error) {
            toast.error("Failed to load user data", {
                duration: 4000,
                style: {
                    background: '#ef4444',
                    color: '#fff',
                    fontSize: '14px',
                    fontWeight: '500',
                    borderRadius: '8px',
                    padding: '16px',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                },
                position: 'top-right',
                icon: '✕',
            });
        }
    };

    useEffect(() => {
        fetchModules();
        fetchRolesModules();

        // If in edit mode, fetch user data
        if (mode === "Edit" && id) {
            fetchUserData();
        }
    }, [id, mode]);

    return (
        <>
            <div className="pb-5 mb-5">
                <form onSubmit={handleSubmit} autoComplete="off">
                    <div className="row">

                        {/* LEFT */}
                        <div className="col-6">
                            <div className="card shadow-sm p-4 border-0 rounded-4">

                                {/* === Personal Info === */}
                                <div className="d-flex align-items-center justify-content-between pb-1">
                                    <SectionHeader title="Personal Details" />
                                    <button
                                        type="button"
                                        className="btn btn-primary rounded userSearchModal"
                                        onClick={() => setShowHrmModal(true)}
                                    >
                                        Search From HRM
                                    </button>
                                </div>
                                <div className="row ">
                                    <div className="col-4">
                                        <h6 className="text-md text-primary-light mb-16">Profile Image</h6>
                                        {/* Upload Image Start */}
                                        <div className="mb-24 mt-16">
                                            <div className="avatar-upload">
                                                <div className="avatar-edit position-absolute bottom-0 end-0 me-24 mt-16 z-1 cursor-pointer">


                                                </div>
                                                <div className="avatar-preview" style={{ width: "190px", height: "190px" }}>
                                                    <div
                                                        id="imagePreview"
                                                        style={{
                                                            backgroundImage: imagePreviewUrl ? `url(${imagePreviewUrl})` : '',

                                                        }}
                                                    >
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Upload Image End */}

                                    </div>
                                    <div className="col-8">
                                        <div className="row">
                                            <div className="col-6">
                                                <label className="form-label fw-semibold ">Salutation<span className="text-danger" >*</span></label>

                                                <Select
                                                    name="salutation"
                                                    options={SALUTATION_OPTIONS}
                                                    placeholder="Select Salutation"
                                                    value={SALUTATION_OPTIONS.find(opt => opt.value === formData.salutation)}
                                                    onChange={(selected) => setFormData((prev) => ({ ...prev, salutation: selected.value }))}
                                                />

                                            </div>

                                            <InputField name="firstName" label="First Name" value={formData.firstName} onChange={handleInputChange} required col="6" error={errors.firstName} />
                                            <InputField name="lastName" label="Last Name" value={formData.lastName} onChange={handleInputChange} col="6" />
                                            <InputField name="phoneNumber" label="Phone Number" type="tel" value={formData.phoneNumber} onChange={handleInputChange} required col="6" error={errors.phoneNumber} />
                                            <InputField name="email" label="Email" type="email" value={formData.email} onChange={handleInputChange} required col="12" error={errors.email} />


                                        </div>

                                    </div>

                                </div>
                                <div className="row">
                                    <InputField name="designation" label="Designation" value={formData.designation} onChange={handleInputChange} required col="6" error={errors.designation} />
                                    <InputField name="address" label="Address" value={formData.address} onChange={handleInputChange} col="6" />
                                    <InputField name="username" label="User Name" type="text" value={formData.username} onChange={handleInputChange} col="6" required error={errors.username} />

                                    <div className="col-6 mb-3">
                                        <label className="form-label fw-semibold">Password {mode !== 'Edit' && <span className="text-danger">*</span>}</label>
                                        <div className="input-group">
                                            <input
                                                type={showPassword ? "text" : "password"}
                                                className={`form-control ${errors.password ? 'is-invalid' : ''}`}
                                                name="password"
                                                value={formData.password}
                                                onChange={handleInputChange}
                                                placeholder="Enter password"
                                            />
                                            <button
                                                type="button"
                                                className="btn btn-outline-secondary"
                                                onClick={() => setShowPassword(!showPassword)}
                                                title={showPassword ? "Hide password" : "Show password"}
                                            >
                                                <Icon icon={showPassword ? "mdi:eye-off" : "mdi:eye"} width="20" />
                                            </button>
                                            <button
                                                type="button"
                                                className="btn btn-outline-secondary"
                                                onClick={copyPasswordToClipboard}
                                                disabled={!formData.password}
                                                title="Copy password to clipboard"
                                            >
                                                <Icon icon="mdi:content-copy" width="20" />
                                            </button>
                                        </div>
                                        {errors.password && <div className="invalid-feedback d-block">{errors.password}</div>}
                                    </div>

                                    <div className="d-flex justify-content-between mb-5 align-items-center w-100">
                                        <small className="text-muted"> {mode === "Edit" ? "Leave password blank to keep existing password." : ""} </small>
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-outline-primary"
                                            onClick={generatePassword}
                                        >
                                            <Icon icon="mdi:refresh" width="16" className="me-1" />
                                            Generate Password
                                        </button>
                                    </div>

                                </div>

                                <button type="submit" className="btn btn-primary w-100" disabled={isSubmitting}>
                                    {isSubmitting ? "Saving..." : "Submit User Details"}
                                </button>

                            </div>

                        </div>

                        {/* RIGHT */}
                        <div className="col-6">
                            <div className="card shadow-sm p-4 border-0 rounded-4">
                                <div>
                                    <SectionHeader title="Roles & Modules" />

                                    <div className="d-flex flex-wrap gap-4 mb-3">
                                        {roleOptions.map(role => (
                                            <div className="form-check d-flex align-items-center" key={role.id}>
                                                <input
                                                    className="form-check-input"
                                                    type="radio"
                                                    name="role"
                                                    value={role.id}
                                                    checked={formData.role === role.id}
                                                    onChange={handleRoleChange}
                                                    id={role.id}
                                                />
                                                <label htmlFor={role.id} className="form-check-label">
                                                    {role.name}
                                                </label>
                                            </div>
                                        ))}

                                    </div>

                                    <Select
                                        isMulti
                                        options={moduleOptions}
                                        value={moduleOptions.filter(o => formData.selectedModules.includes(o.value))}
                                        onChange={handleModulesChange}

                                    />
                                    {errors.role && <div className="text-danger small" >{errors.role}</div>}
                                    {errors.selectedModules && (
                                        <div className="text-danger small mt-1">{errors.selectedModules}</div>
                                    )}

                                </div>
                                <div className="mt-5">
                                    <SectionHeader title="Center Access" />

                                    <div className="d-flex flex-wrap gap-4 mb-3">
                                        {ACCESS_OPTIONS.map(option => (
                                            <div className="form-check d-flex align-items-center" key={option}>
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    name="accessCenter"
                                                    value={option}
                                                    checked={formData.accessCenter.includes(option)}
                                                    onChange={handleAccessTypeChange}
                                                    id={option}
                                                />
                                                <label htmlFor={option} className="form-check-label">
                                                    {option}
                                                </label>
                                            </div>
                                        ))}
                                    </div>

                                    <Select
                                        isMulti
                                        options={centerOptions}
                                        value={centerOptions.filter(o => formData.selectedCenters.includes(o.value))}
                                        onChange={handleCentersChange}
                                    />
                                    {errors.selectedCenters && (<div className="text-danger small"> {errors.selectedCenters}</div>)}


                                </div>



                            </div>
                        </div>
                    </div>
                </form>
            </div>

            {/* === HRM MODAL (UI SAFE) === */}
            {showHrmModal && (
                <>
                    <div className="modal-backdrop fade show w-100 h-100"></div>
                    <div
                        className="modal d-block"
                        tabIndex="-1"
                        style={{ position: "fixed", overflowY: "auto" }}
                    >
                        <div className="modal-dialog modal-dialog-centered">
                            <div className="modal-content rounded-4">
                                <div className="modal-header">
                                    <h5 className="modal-title">Search From HRM</h5>
                                    <button className="btn-close" onClick={() => setShowHrmModal(false)} />
                                </div>
                                {isSearching}

                                <div className="modal-body">

                                    {!hrmUser && (
                                        <input
                                            type="email"
                                            className="form-control"
                                            placeholder="abc@email.com"
                                            value={searchEmail}
                                            onChange={(e) => setSearchEmail(e.target.value)}
                                        />
                                    )}

                                    {isSearching && (
                                        <div className="text-center py-3">
                                            <span className="spinner-border spinner-border-sm"></span>
                                        </div>
                                    )}

                                    {hrmUser && (
                                        <UserProfileInfo
                                            data={hrmUser}
                                            onSelect={handleSelectHrmUser}
                                        />
                                    )}

                                </div>


                                <div className="modal-footer">
                                    <button className="btn btn-secondary" onClick={() => setShowHrmModal(false)}>
                                        Cancel
                                    </button>

                                    {!hrmUser && (
                                        <button
                                            className="btn btn-primary"
                                            onClick={fetchFromHrm}
                                            disabled={isSearching}
                                        >
                                            {isSearching ? "Searching..." : "Search"}
                                        </button>
                                    )}
                                </div>

                            </div>
                        </div>
                    </div>

                </>
            )}
        </>
    );
};

/* === Sub Components === */
const SectionHeader = ({ title }) => (
    <h4 className="border-bottom pb-2 mb-3 mt-4 text-dark fw-semibold">
        {title}
    </h4>
);

const InputField = ({ name, label, value, onChange, type = "text", col = "6", required = false, error }) => (
    <div className={`col-md-${col} mb-3`}>
        <label htmlFor="" className="form-label">{label} {required && <span className="text-danger">*</span>}  </label>
        <input
            className="form-control"
            name={name}
            value={value}
            type={type}
            onChange={onChange}
        />
        {error && <div className="d-block invalid-feedback">{error}</div>}
    </div>
);

const UserProfileInfo = ({ data, onSelect }) => {
    return (
        <div
            className="d-flex align-items-center gap-3 p-3 border rounded cursor-pointer"
            onClick={onSelect}
            style={{ cursor: "pointer" }}
        >
            <img
                src={data.um_image}
                alt={data.username}
                width="64"
                height="64"
                className="rounded-circle object-fit-cover"
            />

            <div>
                <div className="fw-semibold">{data.first_name + " " + data.last_name}</div>
                <div className="text-muted">{data.mobile_no}</div>
            </div>
        </div>
    );
};

export default UserForm;
